import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowLeft as ArrowBackIcon,
  CheckCircle2 as ApproveIcon,
  RotateCw as RegenerateIcon,
  Link as AttachIcon,
  Unlink as DetachIcon,
  Pencil as EditIcon,
  Save as SaveIcon,
  Trash2 as DeleteIcon,
} from 'lucide-react';
import api from '../../utils/axios';
import {
  getNote,
  updateNote,
  approveNote,
  deleteNote,
  regenerateNote,
  attachNoteToClaim,
  detachNoteFromClaim,
} from '../../utils/notesApi';

const RegenerateDialog = ({ open, onClose, onConfirm, regenerating }) => {
  const [feedback, setFeedback] = useState('');

  const handleClose = () => {
    setFeedback('');
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(feedback.trim());
    setFeedback('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Regenerate Note</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Optionally describe what to improve or correct before regenerating.
        </Typography>
        <TextField
          label="Feedback (optional)"
          multiline
          rows={4}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          fullWidth
          placeholder="e.g. Focus more on range of motion improvements, patient reported less pain this session…"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={regenerating}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={regenerating ? <CircularProgress size={16} /> : <RegenerateIcon size={16} />}
          onClick={handleConfirm}
          disabled={regenerating}
        >
          Regenerate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AttachClaimDialog = ({ open, noteId, onClose, onAttached }) => {
  const [claimId, setClaimId] = useState('');
  const [claims, setClaims] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      api.get('/claims/').then((res) => {
        setClaims(res.data.results || res.data);
      }).catch(() => {});
    }
  }, [open]);

  const handleAttach = async () => {
    if (!claimId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await attachNoteToClaim(noteId, claimId);
      onAttached(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to attach note to claim.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Attach to Claim</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          select
          label="Select Claim"
          value={claimId}
          onChange={(e) => setClaimId(e.target.value)}
          fullWidth
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">-- Select --</option>
          {claims.map((c) => {
            const label =
              `${c.patient_display_name || 'Unknown patient'} — ${c.earliest_service_date || 'No service date'}`
              + (c.payer_name ? ` (${c.payer_name})` : '');
            return (
              <option key={c.id} value={c.id}>
                {label}
              </option>
            );
          })}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleAttach} disabled={!claimId || saving}>
          {saving ? <CircularProgress size={20} /> : 'Attach'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ContextPanel = ({ note, serviceDate }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle2" gutterBottom>
      Context Used
    </Typography>
    <Divider sx={{ mb: 1.5 }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="body2">
        <strong>Service Date:</strong> {serviceDate || note.date}
      </Typography>
      <Typography variant="body2">
        <strong>Generated By:</strong> {note.generated_by === 'system' ? 'AI' : 'Provider'}
      </Typography>
      {note.appointment && (
        <Typography variant="body2">
          <strong>Appointment:</strong> {note.appointment}
        </Typography>
      )}
      {note.claim && (
        <Typography variant="body2">
          <strong>Attached Claim:</strong> {note.claim}
        </Typography>
      )}
      {note.approved_by && (
        <Typography variant="body2">
          <strong>Approved By:</strong> {note.approved_by}
        </Typography>
      )}
      {note.approved_at && (
        <Typography variant="body2">
          <strong>Approved At:</strong> {new Date(note.approved_at).toLocaleDateString()}
        </Typography>
      )}
    </Box>
  </Paper>
);

const NoteReviewPage = () => {
  const { noteId } = useParams();
  const navigate = useNavigate();

  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);

  const goBackToPatientHistory = useCallback(() => {
    if (note?.patient) {
      navigate(`/notes/patients/${note.patient}`, {
        state: { refreshAt: Date.now() },
      });
      return;
    }
    navigate(-1);
  }, [navigate, note]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNote(noteId);
      setNote(res.data);
      setEditedText(res.data.text);
      setEditedDate(res.data.date);
    } catch (err) {
      setError('Failed to load note.');
    } finally {
      setLoading(false);
    }
  }, [noteId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll every 3 s while the worker is generating the note
  useEffect(() => {
    if (note?.status !== 'generating') return;
    const interval = setInterval(async () => {
      try {
        const res = await getNote(noteId);
        if (res.data.status !== 'generating') {
          setNote(res.data);
          setEditedText(res.data.text);
          setEditedDate(res.data.date);
        }
      } catch (_) {
        // ignore transient poll errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [note?.status, noteId]);

  const handleSaveEdit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await updateNote(noteId, { text: editedText, date: editedDate });
      setNote(res.data);
      setEditedDate(res.data.date);
      setEditing(false);
      setSuccessMsg('Note saved.');
    } catch (err) {
      setError('Failed to save note.');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    setError(null);
    try {
      // Save any pending date/text changes first, then approve
      const hasDateChange = editedDate && editedDate !== note.date;
      const hasTextChange = editing && editedText !== note.text;
      if (hasDateChange || hasTextChange) {
        const patch = {};
        if (hasDateChange) patch.date = editedDate;
        if (hasTextChange) patch.text = editedText;
        await updateNote(noteId, patch);
      }
      // Don't re-send text if already patched above
      const payload = (editing && !hasTextChange) ? { text: editedText } : {};
      const res = await approveNote(noteId, payload);
      setNote(res.data);
      setEditedDate(res.data.date);
      setEditing(false);
      setSuccessMsg('Note approved and added to patient history.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve note.');
    } finally {
      setApproving(false);
    }
  };

  const handleRegenerate = async (feedback = '') => {
    setRegenerateDialogOpen(false);
    setRegenerating(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await regenerateNote(noteId, feedback ? { quick_facts: feedback } : {});
      setNote(res.data);
      setEditedText(res.data.text);
      setEditing(false);
      setSuccessMsg('Note regenerated.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to regenerate note.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteNote(noteId);
      goBackToPatientHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete note.');
      setDeleting(false);
    }
  };

  const handleDetach = async () => {
    setError(null);
    try {
      const res = await detachNoteFromClaim(noteId);
      setNote(res.data);
      setSuccessMsg('Note detached from claim.');
    } catch (err) {
      setError('Failed to detach note from claim.');
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const isApproved = note?.status === 'approved';
  const isDraft = note?.status === 'draft';
  const isGenerating = note?.status === 'generating';
  const isFailed = note?.status === 'failed';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton
          size="small"
          onClick={goBackToPatientHistory}
        >
          <ArrowBackIcon size={16} />
        </IconButton>
        <Typography variant="h5">
          {isApproved ? 'Approved Note' : isGenerating ? 'Generating Note…' : isFailed ? 'Generation Failed' : 'Review Draft Note'}
        </Typography>
        <Chip
          label={isApproved ? 'Approved' : isGenerating ? 'Generating' : isFailed ? 'Failed' : 'Draft'}
          color={isApproved ? 'success' : isGenerating ? 'info' : isFailed ? 'error' : 'info'}
          size="small"
          icon={isGenerating ? <CircularProgress size={12} color="inherit" /> : undefined}
          sx={{ ml: 1 }}
        />
      </Box>
      {note && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 5 }}>
          Service date: {editedDate && editedDate !== note.date ? (
            <><span style={{ textDecoration: 'line-through', opacity: 0.5 }}>{note.date}</span>{' → '}{editedDate}</>
          ) : note.date}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Note text */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Note Text
              </Typography>
              {!editing && !isGenerating && (
                <Button
                  size="small"
                  startIcon={<EditIcon size={16} />}
                  onClick={() => { setEditing(true); setEditedText(note.text); setEditedDate(note.date); }}
                >
                  Edit
                </Button>
              )}
              {editing && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon size={16} />}
                    onClick={handleSaveEdit}
                    disabled={saving}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Box>

            {isGenerating && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  The AI is writing this note. This page will update automatically.
                </Typography>
              </Box>
            )}

            {isFailed && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Note generation failed. Use <strong>Regenerate</strong> below to try again.
              </Alert>
            )}

            {editing && isApproved && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are editing an approved note. Saving will update the record but keep its approved status.
              </Alert>
            )}
            {editing && (
              <TextField
                label="Service Date"
                type="date"
                value={editedDate}
                onChange={(e) => setEditedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ mb: 2 }}
              />
            )}
            {editing ? (
              <TextField
                multiline
                fullWidth
                minRows={12}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
            ) : !isGenerating && (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
              >
                {note?.text}
              </Typography>
            )}
          </Paper>

          {/* Actions */}
          {note && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {(isDraft || isFailed) && (
                <>
                  {!editing && (
                    <TextField
                      label="Service Date"
                      type="date"
                      value={editedDate}
                      onChange={(e) => setEditedDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: 160 }}
                    />
                  )}
                  {isDraft && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={approving ? <CircularProgress size={16} /> : <ApproveIcon size={16} />}
                    onClick={handleApprove}
                    disabled={approving || regenerating}
                  >
                    Approve Note
                  </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={regenerating ? <CircularProgress size={16} /> : <RegenerateIcon size={16} />}
                    onClick={() => setRegenerateDialogOpen(true)}
                    disabled={regenerating || approving}
                  >
                    Regenerate
                  </Button>
                </>
              )}

              {!note.claim ? (
                <Button
                  variant="outlined"
                  startIcon={<AttachIcon size={16} />}
                  onClick={() => setAttachDialogOpen(true)}
                >
                  Attach to Claim
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<DetachIcon size={16} />}
                  onClick={handleDetach}
                >
                  Detach from Claim
                </Button>
              )}

              <Button
                variant="outlined"
                color="error"
                startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon size={16} />}
                onClick={handleDelete}
                disabled={deleting}
                sx={{ ml: 'auto' }}
              >
                Delete Note
              </Button>
            </Box>
          )}
        </Box>

        {/* Context sidebar */}
        {note && (
          <Box sx={{ width: 220, flexShrink: 0 }}>
            <ContextPanel note={note} serviceDate={editedDate} />
          </Box>
        )}
      </Box>

      <RegenerateDialog
        open={regenerateDialogOpen}
        onClose={() => setRegenerateDialogOpen(false)}
        onConfirm={handleRegenerate}
        regenerating={regenerating}
      />

      <AttachClaimDialog
        open={attachDialogOpen}
        noteId={noteId}
        onClose={() => setAttachDialogOpen(false)}
        onAttached={(updated) => {
          setNote(updated);
          setSuccessMsg('Note attached to claim.');
        }}
      />
    </Container>
  );
};

export default NoteReviewPage;
